import React, { useEffect, useRef, useState } from "react";
import { FaClock } from "react-icons/fa";
import CountdownBlocks, { countdownLabels } from "./CountdownBlocks";
import { calculateRemainingTime } from "./ActivationCountdown";
import { toTimestamp } from "../../utils/roleDocuments";
import "./Timer.css";

const safeLanguage = (infoSource, section, key) => {
  try {
    return infoSource?.language?.(infoSource, section, key) || "";
  } catch (error) {
    return "";
  }
};

/**
 * Countdown to `enDate` (a Date, an ISO string or a millisecond timestamp).
 *
 * Props kept from the previous class component:
 *   enDate, nbrdays (a non-positive value still means "already expired"),
 *   userExit (show the expired message, default true), messageWelcome +
 *   message (welcome box once expired), infoSource.
 * New: onExpire() called once when the countdown reaches zero.
 *
 * Display only -- every expiry is re-checked by the backend.
 */
const Timer = ({ enDate, nbrdays, userExit = true, messageWelcome, message, infoSource, onExpire }) => {
  const target = toTimestamp(enDate);
  const [remaining, setRemaining] = useState(() => (target ? calculateRemainingTime(target) : null));
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    if (!target) {
      setRemaining(null);
      return undefined;
    }
    const tick = () => setRemaining(calculateRemainingTime(target));
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [target]);

  const nbrdaysExpired = typeof nbrdays === "number" && Number.isFinite(nbrdays) && nbrdays <= 0;
  const expired = !target || !remaining || remaining.expired || nbrdaysExpired;

  useEffect(() => {
    if (expired && target && !firedRef.current) {
      firedRef.current = true;
      onExpire?.();
    }
  }, [expired, target, onExpire]);

  if (expired && messageWelcome) {
    return (
      <div className="timer-welcome" role="status">
        <h1>{message?.accountTitle}</h1>
      </div>
    );
  }

  if (expired) {
    if (!userExit) return null;
    return (
      <div className="timer-expired" role="status">
        <FaClock aria-hidden="true" />
        <p>{safeLanguage(infoSource, "title", "compExp")}</p>
      </div>
    );
  }

  const severity = remaining.totalMilliseconds <= 3600000 ? "critical" : remaining.totalMilliseconds <= 86400000 ? "warning" : "normal";
  return <CountdownBlocks remaining={remaining} labels={countdownLabels(infoSource)} severity={severity} />;
};

export default Timer;
